import React, { useState, useId } from "react";
import { cn } from "@/lib/utils";

export interface ChartSlice {
  label: string;
  value: number;
  color?: string;
  sublabel?: string;
}

const DEFAULT_PALETTE = [
  "#2563eb", // blue-600
  "#059669", // emerald-600
  "#7c3aed", // violet-600
  "#d97706", // amber-600
  "#0891b2", // cyan-600
  "#ea580c", // orange-600
  "#e11d48", // rose-600
  "#64748b", // slate-500
];

interface DonutPieChartProps {
  data: ChartSlice[];
  donut?: boolean;
  size?: number;
  centerValue?: string;
  centerLabel?: string;
  formatValue?: (v: number) => string;
  onSliceClick?: (slice: ChartSlice) => void;
  className?: string;
  showLegend?: boolean;
}

function polarToCartesian(centerX: number, centerY: number, radius: number, angleInDegrees: number) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}

function describeArc(x: number, y: number, radius: number, innerRadius: number, startAngle: number, endAngle: number) {
  // Clamp angle delta to slightly less than 360 to prevent SVG rendering artifacts on complete circles
  const delta = Math.min(359.999, endAngle - startAngle);
  const effectiveEnd = startAngle + delta;

  const start = polarToCartesian(x, y, radius, effectiveEnd);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = delta <= 180 ? "0" : "1";

  if (innerRadius <= 0) {
    return [
      "M", x, y,
      "L", start.x, start.y,
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
      "Z",
    ].join(" ");
  }

  const innerStart = polarToCartesian(x, y, innerRadius, startAngle);
  const innerEnd = polarToCartesian(x, y, innerRadius, effectiveEnd);

  return [
    "M", start.x, start.y,
    "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
    "L", innerStart.x, innerStart.y,
    "A", innerRadius, innerRadius, 0, largeArcFlag, 1, innerEnd.x, innerEnd.y,
    "Z",
  ].join(" ");
}

export function DonutPieChart({
  data,
  donut = true,
  size = 220,
  centerValue,
  centerLabel,
  formatValue = (v) => v.toLocaleString("en-IN"),
  onSliceClick,
  className,
  showLegend = true,
}: DonutPieChartProps) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const filterId = useId();

  const total = data.reduce((sum, item) => sum + (Number(item.value) || 0), 0);

  if (!data || data.length === 0 || total === 0) {
    return (
      <div className={cn("flex flex-col items-center justify-center p-6 text-center text-xs text-muted-foreground", className)}>
        <div className="size-20 rounded-full border-4 border-dashed border-border/80 flex items-center justify-center mb-2">
          <span className="text-[11px] font-semibold text-muted-foreground/60">No Data</span>
        </div>
        <p className="font-medium">No analytics available for this period</p>
      </div>
    );
  }

  const center = size / 2;
  const radius = (size / 2) - 12;
  const innerRadius = donut ? radius * 0.64 : 0;

  // Build slice angles
  let currentAngle = 0;
  const slices = data.map((item, idx) => {
    const val = Number(item.value) || 0;
    const fraction = total > 0 ? val / total : 0;
    const sweep = fraction * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + sweep;
    currentAngle += sweep;
    const color = item.color || DEFAULT_PALETTE[idx % DEFAULT_PALETTE.length];
    return {
      ...item,
      val,
      percentage: (fraction * 100).toFixed(1),
      startAngle,
      endAngle,
      color,
    };
  });

  const activeSlice = hoveredIdx !== null ? slices[hoveredIdx] : null;

  return (
    <div className={cn("flex flex-wrap items-center justify-center gap-x-6 gap-y-4", className)}>
      {/* SVG Donut / Pie */}
      <div className="relative shrink-0 flex items-center justify-center" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="overflow-visible select-none">
          <defs>
            <filter id={`shadow-${filterId}`} x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.25" />
            </filter>
          </defs>

          {slices.map((slice, i) => {
            const isHovered = hoveredIdx === i;
            const currentR = isHovered ? radius + 4 : radius;
            const currentInnerR = isHovered && donut ? Math.max(0, innerRadius - 2) : innerRadius;
            const d = describeArc(center, center, currentR, currentInnerR, slice.startAngle, slice.endAngle);

            return (
              <path
                key={slice.label + i}
                d={d}
                fill={slice.color}
                className="transition-all duration-200 cursor-pointer"
                style={{
                  transformOrigin: `${center}px ${center}px`,
                  opacity: hoveredIdx !== null && !isHovered ? 0.6 : 1,
                  filter: isHovered ? `url(#shadow-${filterId})` : undefined,
                }}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                onClick={() => onSliceClick?.(slice)}
              />
            );
          })}
        </svg>

        {/* Center Text for Donut */}
        {donut && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none px-3"
            style={{ width: size, height: size }}
          >
            {activeSlice ? (
              <>
                <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider truncate max-w-[110px]">
                  {activeSlice.label}
                </span>
                <span className="text-base font-black text-foreground tabular-nums">
                  {formatValue(activeSlice.val)}
                </span>
                <span className="text-[10px] font-semibold text-primary">
                  {activeSlice.percentage}%
                </span>
              </>
            ) : (
              <>
                {centerValue && (
                  <span className="text-base sm:text-lg font-black text-foreground tracking-tight tabular-nums">
                    {centerValue}
                  </span>
                )}
                {centerLabel && (
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mt-0.5">
                    {centerLabel}
                  </span>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Legend list */}
      {showLegend && (
        <div className="flex flex-col gap-1.5 w-full sm:max-w-xs text-xs">
          {slices.map((slice, i) => {
            const isHovered = hoveredIdx === i;
            return (
              <div
                key={slice.label + i}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                onClick={() => onSliceClick?.(slice)}
                className={cn(
                  "flex items-center justify-between gap-2.5 px-2.5 py-1.5 rounded-lg transition-colors cursor-pointer select-none",
                  isHovered ? "bg-muted shadow-2xs" : "hover:bg-muted/50"
                )}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="size-2.5 rounded-full shrink-0 shadow-2xs"
                    style={{ backgroundColor: slice.color }}
                  />
                  <span className="truncate font-medium text-foreground text-xs" title={slice.label}>
                    {slice.label}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0 tabular-nums">
                  <span className="font-semibold text-foreground text-xs">
                    {formatValue(slice.val)}
                  </span>
                  <span className="text-[10px] font-medium text-muted-foreground w-9 text-right">
                    {slice.percentage}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
